import type { FormEvent } from 'react'
import type { DemoSourceMode, DemoToneAnalysisErrors, DemoToneAnalysisFormValues } from '../../models/demoToneAnalysis'
import type { VideoPreview, VideoPreviewStatus } from '../../models/videoPreview'
import { derivePresetBaseName } from '../../utils/youtubePreviewMetadata'
import { AnalysisSourceSelector } from './AnalysisSourceSelector'

export type ToneAnalysisFormValues = DemoToneAnalysisFormValues

type ToneAnalysisFormProps = {
  values: ToneAnalysisFormValues
  errors: DemoToneAnalysisErrors
  preview: VideoPreview | null
  previewStatus: VideoPreviewStatus
  previewError: string
  isLoading: boolean
  canAnalyze: boolean
  onChange: (values: ToneAnalysisFormValues) => void
  onClearSourceErrors: () => void
  onMetadataFieldEdit: (field: 'songTitle' | 'artist' | 'presetBaseName') => void
  onRetryPreview: () => void
  onSubmit: () => void
}

export function ToneAnalysisForm({
  values,
  errors,
  preview,
  previewStatus,
  previewError,
  isLoading,
  canAnalyze,
  onChange,
  onClearSourceErrors,
  onMetadataFieldEdit,
  onRetryPreview,
  onSubmit,
}: ToneAnalysisFormProps) {
  function updateValue<TKey extends keyof ToneAnalysisFormValues>(
    key: TKey,
    value: ToneAnalysisFormValues[TKey],
  ) {
    onChange({ ...values, [key]: value })
  }

  function handleSongTitleChange(songTitle: string) {
    onMetadataFieldEdit('songTitle')
    onChange({
      ...values,
      songTitle,
      presetBaseName: values.presetBaseName.trim() ? values.presetBaseName : derivePresetBaseName(songTitle),
    })
  }

  function handleSourceModeChange(sourceMode: DemoSourceMode) {
    onClearSourceErrors()
    onChange({ ...values, sourceMode })
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    onSubmit()
  }

  return <form className="ts-analysis-form" onSubmit={handleSubmit}>
    <span className="ts-demo-badge">INTERACTIVE TECHNOLOGY PREVIEW</span>
    <h3>Analyze a song with the current ToneSignature engine.</h3>
    <p>YouTube previews are loaded through the backend, then ToneSignature looks up the song in the Knowledge Database. No file contents are uploaded.</p>

    <AnalysisSourceSelector
      mode={values.sourceMode}
      youtubeUrl={values.youtubeUrl}
      errors={{
        youtubeUrl: errors.youtubeUrl,
        librarySearch: errors.librarySearch,
      }}
      libraryArtist={values.libraryArtist}
      libraryTitle={values.libraryTitle}
      preview={preview}
      previewStatus={previewStatus}
      previewError={previewError}
      disabled={isLoading}
      onModeChange={handleSourceModeChange}
      onYoutubeUrlChange={(youtubeUrl) => {
        onClearSourceErrors()
        updateValue('youtubeUrl', youtubeUrl)
      }}
      onLibraryArtistChange={(libraryArtist) => {
        onClearSourceErrors()
        onMetadataFieldEdit('artist')
        onChange({ ...values, libraryArtist, artist: libraryArtist })
      }}
      onLibraryTitleChange={(libraryTitle) => {
        onClearSourceErrors()
        onMetadataFieldEdit('songTitle')
        onChange({
          ...values,
          libraryTitle,
          songTitle: libraryTitle,
          presetBaseName: values.presetBaseName.trim() ? values.presetBaseName : derivePresetBaseName(libraryTitle),
        })
      }}
      onRetryPreview={onRetryPreview}
    />

    <div className="ts-analysis-form-grid">
      <label>
        <span>Song title</span>
        <input
          value={values.songTitle}
          onChange={(event) => handleSongTitleChange(event.target.value)}
          disabled={isLoading}
        />
        {errors.songTitle && <small role="alert">{errors.songTitle}</small>}
      </label>

      <label>
        <span>Artist</span>
        <input
          value={values.artist}
          onChange={(event) => {
            onMetadataFieldEdit('artist')
            updateValue('artist', event.target.value)
          }}
          disabled={isLoading}
        />
      </label>

      <label>
        <span>Preset base name</span>
        <input
          value={values.presetBaseName}
          onChange={(event) => {
            onMetadataFieldEdit('presetBaseName')
            updateValue('presetBaseName', event.target.value)
          }}
          disabled={isLoading}
        />
        {errors.presetBaseName && <small role="alert">{errors.presetBaseName}</small>}
      </label>

      <label>
        <span>Guitar layers</span>
        <select
          value={values.maximumGuitarLayers}
          onChange={(event) => updateValue('maximumGuitarLayers', Number(event.target.value) as 1 | 2)}
          disabled={isLoading}
        >
          <option value={1}>Rhythm only</option>
          <option value={2}>Rhythm + Lead</option>
        </select>
        {errors.maximumGuitarLayers && <small role="alert">{errors.maximumGuitarLayers}</small>}
      </label>
    </div>

    <button className="button ts-demo-analyze" type="submit" disabled={isLoading || !canAnalyze}>
      {isLoading ? 'Analyzing...' : values.sourceMode === 'library' ? 'Search Tone Library' : 'Analyze Tone'}
    </button>
  </form>
}
